import { FileCheck2 } from 'lucide-react';
import { useState } from 'react';
import { FormField } from '@/components/shared/form-field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export type AndroidDraft = { enabled: boolean; service_account_json: string };
export type IosDraft = { enabled: boolean; key_id: string; team_id_apple: string; bundle_id: string; private_key_p8: string };
export const emptyAndroid = (): AndroidDraft => ({ enabled: true, service_account_json: '' });
export const emptyIos = (): IosDraft => ({ enabled: true, key_id: '', team_id_apple: '', bundle_id: '', private_key_p8: '' });

const readFile = (f: File) => f.text();

/** Secrets are read client-side and sent once; they are never shown again (write-only, §10.4). */
export function AndroidForm({ value, onChange }: { value: AndroidDraft; onChange(v: AndroidDraft): void }) {
  const [projectId, setProjectId] = useState<string | null>(null);
  const set = (json: string) => {
    onChange({ ...value, service_account_json: json });
    try {
      setProjectId((JSON.parse(json) as { project_id?: string }).project_id ?? null);
    } catch {
      setProjectId(null);
    }
  };
  return (
    <div className="grid gap-3">
      <FormField
        label="Firebase service account JSON"
        hint={projectId
          ? <span className="inline-flex items-center gap-1 text-success"><FileCheck2 className="size-3.5" /> Project detected: <b className="font-mono">{projectId}</b></span>
          : 'Firebase Console → Project settings → Service accounts → Generate new private key.'}
      >
        <Input type="file" accept=".json,application/json" className="cursor-pointer" onChange={(e) => e.target.files?.[0] && void readFile(e.target.files[0]).then(set)} />
      </FormField>
      <Textarea rows={4} className="font-mono text-xs" placeholder='…or paste: { "type": "service_account", "project_id": … }' value={value.service_account_json} onChange={(e) => set(e.target.value)} />
    </div>
  );
}

export function IosForm({ value, onChange }: { value: IosDraft; onChange(v: IosDraft): void }) {
  const up = (patch: Partial<IosDraft>) => onChange({ ...value, ...patch });
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Key ID"><Input className="font-mono" placeholder="ABC123XYZ0" value={value.key_id} onChange={(e) => up({ key_id: e.target.value.toUpperCase() })} /></FormField>
        <FormField label="Team ID"><Input className="font-mono" placeholder="DEF456GHI0" value={value.team_id_apple} onChange={(e) => up({ team_id_apple: e.target.value.toUpperCase() })} /></FormField>
        <FormField label="Bundle ID"><Input className="font-mono" placeholder="com.example.app" value={value.bundle_id} onChange={(e) => up({ bundle_id: e.target.value })} /></FormField>
      </div>
      <p className="text-xs text-muted-foreground">
        The same key works for Xcode (sandbox) and TestFlight / App Store (production) builds. Each device reports which one it uses when it registers.
      </p>
      <FormField label="Private key (.p8)" hint="Apple lets you download it once — keep a copy in your secrets manager.">
        <Input type="file" accept=".p8" className="cursor-pointer" onChange={(e) => e.target.files?.[0] && void readFile(e.target.files[0]).then((t) => up({ private_key_p8: t }))} />
      </FormField>
      <Textarea rows={3} className="font-mono text-xs" placeholder="…or paste -----BEGIN PRIVATE KEY-----" value={value.private_key_p8} onChange={(e) => up({ private_key_p8: e.target.value })} />
    </div>
  );
}
